import {produce} from 'immer';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useMetricSearchQuery} from '../../../../generated/graphql';
import {useProjectParams} from '../../../../pages/ProjectPage/useProjectParams';
import {
  type ApolloError,
  NetworkStatus,
} from '../../../../state/graphql/apolloWithAbort';
import {
  type RunHistoryKeyInfoKeys,
  type RunHistoryKeyType,
} from '../../../../types/run';
import {useNgwTruncatedPanelGenLimits} from '../../../../util/dynamicConfigs/ngwTruncatedPanelGenLimits';
import {
  extractMetricSearchErrorMessage,
  propagateErrorsContext,
} from '../../../../util/errors';
import {toMongo} from '../../../../util/filters';
import {
  getExpectedPanelSpecs,
  keyInfoToDefaultPanelSpecs,
} from '../../../../util/panelbank';
import * as QueryTS from '../../../../util/queryts';
import {
  collapseMetricTypes,
  type CollapsibleMetricTypes,
} from '../../../MetricSearch/collapseMetricTypes';
import {useWorkspaceHistoryKeyInfoQueryVars} from '../../../MultiRunWorkspace/useWorkspaceHistoryKeyInfoQueryVars';
import {useExpectedPanelSpecsInputs} from '../../../PanelBank/hooks/useExpectedPanelSpecsInputs';
import {useAutoOrganizePrefix} from '../../Settings/hooks';
import {type QuickAddKeys} from '../PanelsQuickAddContext';

/**
 * Converts results of RSA APIs (e.g. metric search, SAHK) to RunHistoryKeyInfoKeys format
 * @param metricSearchResults - The metric search results from the GraphQL query
 * @returns RunHistoryKeyInfoKeys object
 *
 * NOTE: These APIs only give us key + type (no type counts), so every `count` is a
 * placeholder value of 1. Additionally, built-in `_runtime`/`_timestamp`/`_step` keys
 * are absent. This is fine for current consumers (e.g. auto panel gen, panel quick add),
 * which either ignore counts or re-add the reserved x-axis keys themselves.
 * Do NOT use RSA results for consumers that rely on real type counts.
 */
export function convertMetricSearchResultsToKeyInfo(
  metricSearchResults: Array<{key: string} & CollapsibleMetricTypes>
): RunHistoryKeyInfoKeys {
  const keyInfo: RunHistoryKeyInfoKeys = {};

  for (const searchResult of metricSearchResults) {
    const type = collapseMetricTypes(searchResult);
    if (type == null) {
      continue;
    }

    keyInfo[searchResult.key] = {
      typeCounts: [
        {
          type: type as RunHistoryKeyType,
          count: 1, // Default count since metric search doesn't provide counts
        },
      ],
    };
  }

  return keyInfo;
}

export const useSearchedKeys = (
  searchStr: string,
  keyType?: RunHistoryKeyType,
  enabled = true
) => {
  const expectedPanelSpecsInputs = useExpectedPanelSpecsInputs();

  // Each consumer computes its own list and frees it when it unmounts. The
  // specs hold one entry per key, so they are only built while enabled.
  const sortedQuickAddKeys = useMemo(
    function memoSortedQuickAddKeys(): QuickAddKeys {
      if (!expectedPanelSpecsInputs || !enabled) {
        return [];
      }
      const {defaultSpecs, apiAddedSpecs} = getExpectedPanelSpecs(
        expectedPanelSpecsInputs
      );
      return [
        ...Object.entries(defaultSpecs),
        ...Object.entries(apiAddedSpecs),
      ].sort((a, b) => a[0].localeCompare(b[0]));
    },
    [enabled, expectedPanelSpecsInputs]
  );

  const allQuickAddKeys = useMemo(
    function memoAllQuickAddKeys() {
      return keyType
        ? sortedQuickAddKeys.filter(([_, spec]) => spec.keyType === keyType)
        : sortedQuickAddKeys;
    },
    [sortedQuickAddKeys, keyType]
  );

  return useMemo(() => {
    try {
      if (!searchStr) {
        return allQuickAddKeys;
      }
      const regex = new RegExp(searchStr, 'i');
      return allQuickAddKeys.filter(([key]) => regex.test(key));
    } catch (e) {
      return [];
    }
  }, [allQuickAddKeys, searchStr]);
};

export const useNextGenSearchedKeys = (
  searchStr: string,
  pageSize: number
): {
  keys: QuickAddKeys;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  retryInitialFetch: () => void;
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  initialError: Error | undefined;
  searchErrorMessage: string | undefined;
  loadMoreError: Error | undefined;
} => {
  const {
    entityName,
    projectName,
    filters,
    sort,
    requiredRunKeys = [],
  } = useWorkspaceHistoryKeyInfoQueryVars();
  const {
    config: {runLimit},
  } = useNgwTruncatedPanelGenLimits();
  const autoOrganizePrefix = useAutoOrganizePrefix();
  const serializedFilters = JSON.stringify(toMongo(filters));
  const runOrder = QueryTS.sortToOrderString(sort);
  const metricNamePattern = searchStr.trim() !== '' ? searchStr : undefined;
  const isFetchingMoreRef = useRef(false);
  const [loadMoreError, setLoadMoreError] = useState<Error>();

  const {data, loading, error, fetchMore, networkStatus, refetch} =
    useMetricSearchQuery({
      variables: {
        projectName,
        entityName,
        filters: serializedFilters,
        runLimit,
        runOrder,
        requiredRunKeys:
          requiredRunKeys.length > 0 ? requiredRunKeys : undefined,
        keysPageSize: pageSize,
        metricNamePattern,
      },
      context: propagateErrorsContext(),
      notifyOnNetworkStatusChange: true,
    });

  const metricSearch = data?.project?.runs?.metricSearch;
  const nextCursor = metricSearch?.cursor;
  const hasNextPage = metricSearch?.hasMore ?? false;

  // query key is captured via closure in fetchNextPage to check race condition
  const queryKey = JSON.stringify([
    projectName,
    entityName,
    serializedFilters,
    runLimit,
    runOrder,
    requiredRunKeys,
    metricNamePattern,
    pageSize,
  ]);
  const activeQueryKeyRef = useRef(queryKey);
  // oxlint-disable-next-line react/refs
  activeQueryKeyRef.current = queryKey;

  useEffect(() => {
    setLoadMoreError(undefined);
  }, [queryKey]);

  const fetchNextPage = useCallback(() => {
    if (!hasNextPage || nextCursor == null || isFetchingMoreRef.current) {
      return;
    }

    setLoadMoreError(undefined);
    isFetchingMoreRef.current = true;
    const requestedQueryKey = queryKey;

    void fetchMore({
      variables: {
        projectName,
        entityName,
        filters: serializedFilters,
        runLimit,
        runOrder,
        requiredRunKeys:
          requiredRunKeys.length > 0 ? requiredRunKeys : undefined,
        keysPageSize: pageSize,
        metricNamePattern,
        cursor: nextCursor,
      },
      updateQuery: (previousResult, {fetchMoreResult}) => {
        if (activeQueryKeyRef.current !== requestedQueryKey) {
          // race condition, if a user types while "search A" is in flight and fires a new "search B" query,
          // we don't want to update previous result with the new search B results.
          return previousResult;
        }

        return produce(previousResult, draft => {
          const current = draft.project?.runs?.metricSearch;
          const next = fetchMoreResult.project?.runs?.metricSearch;

          if (!current || !next) {
            return;
          }

          current.results.push(...next.results);
          current.cursor = next.cursor;
          current.hasMore = next.hasMore;
        });
      },
    })
      .catch(fetchError => {
        if (activeQueryKeyRef.current !== requestedQueryKey) {
          return;
        }

        setLoadMoreError(
          fetchError instanceof Error
            ? fetchError
            : new Error('Failed to load more keys')
        );
      })
      .finally(() => {
        isFetchingMoreRef.current = false;
      });
    // oxlint-disable-next-line react/preserve-manual-memoization
  }, [
    entityName,
    fetchMore,
    hasNextPage,
    metricNamePattern,
    nextCursor,
    pageSize,
    projectName,
    requiredRunKeys,
    runLimit,
    runOrder,
    serializedFilters,
    queryKey,
  ]);

  const retryInitialFetch = useCallback(() => {
    void refetch().catch(() => {
      // apollo exposes the error through the query result.
    });
  }, [refetch]);

  const quickKeys: QuickAddKeys = useMemo(() => {
    if (!metricSearch?.results) {
      return [];
    }

    const keysInfo = convertMetricSearchResultsToKeyInfo(metricSearch.results);

    // Create default panel specs from the key info
    const defaultPanelSpecs = keyInfoToDefaultPanelSpecs(
      keysInfo,
      {},
      autoOrganizePrefix
    );

    // Transform defaultPanelSpecs to QuickAddKeys format
    return Object.entries(defaultPanelSpecs).map(([key, spec]) => [key, spec]);
  }, [metricSearch, autoOrganizePrefix]);

  const isInitialLoading = loading && metricSearch == null;
  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;
  const initialError = metricSearch == null ? error : undefined;
  const searchErrorMessage =
    metricNamePattern == null || networkStatus === NetworkStatus.setVariables
      ? undefined
      : extractMetricSearchErrorMessage(error);

  return {
    keys: quickKeys,
    hasNextPage,
    fetchNextPage,
    retryInitialFetch,
    isInitialLoading,
    isLoadingMore,
    // Unsupported RE2 syntax is a user-correctable search error. Let the
    // search field render its message instead of the generic retry state.
    initialError: searchErrorMessage == null ? initialError : undefined,
    searchErrorMessage,
    loadMoreError,
  };
};
